import React, { useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { DocumentStakeholder } from "../../@types/documentStakeholder";
import { notificationsOutline, mailOutline, copyOutline, logoWhatsapp } from "ionicons/icons";
import { notifySigner } from "../../store/documentShow/actions/notifySigner";
import { useDispatch, useSelector } from "react-redux";
import { notifySignerByWhatsapp } from "../../store/documentShow/actions/notifySignerByWhatsapp";
import { RootState } from "../../store/rootReducer";

interface ComponentProps {
  documentStakeholder: DocumentStakeholder;
}

export const DocumentStakeholderNotify: React.FC<ComponentProps> = ({ documentStakeholder }) => {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.documentShow);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [presentToast, hideToast] = useIonToast();
  const urlNotify = `https://mi.guardservice.cl/app/acuerdos/firma/${documentStakeholder?.document.id}`;

  const handleSendNotification = async () => {
    try {
      await hideToast();
      presentToast({
        message: `Reenviando notificación...`,
        color: "primary",
      });
      const { payload: notificationCreated }: any =
        (await dispatch(notifySigner(documentStakeholder))) || {};
      if (!notificationCreated) {
        throw new Error("No se envió la notificación");
      }

      await hideToast();
      presentToast({
        message: `Se envió una notificación a ${documentStakeholder.stakeholder?.name} para que firme el acuerdo`,
        color: "success",
        duration: 8000,
      });
    } catch (error) {
      await hideToast();
      presentToast({
        message: "Ocurrió un problema enviando notificación al firmante",
        color: "danger",
        duration: 8000,
      });
    }
  };

  const handleSendWhatsappNotification = async () => {
    try {
      await hideToast();
      presentToast({
        message: `Reenviando notificación...`,
        color: "primary",
        duration: 8000,
      });
      const { payload: notificationCreated }: any =
        (await dispatch(notifySignerByWhatsapp({ documentStakeholder, typeNotification: 1 }))) ||
        {};
      if (!notificationCreated) {
        throw new Error("No se envió la notificación");
      }

      await hideToast();
      presentToast({
        message: `Se envió una notificación a ${documentStakeholder.stakeholder?.name} para que firme el acuerdo`,
        color: "success",
        duration: 8000,
      });
    } catch (error) {
      await hideToast();
      presentToast({
        message: "Ocurrió un problema enviando notificación al firmante",
        color: "danger",
        duration: 8000,
      });
    }
  };

  const handleSendAllWhatsappNotification = async () => {
    try {
      await hideToast();
      presentToast({
        message: `Reenviando notificación...`,
        color: "primary",
        duration: 8000,
      });
      const { payload: notificationCreated }: any =
        (await dispatch(notifySignerByWhatsapp({ documentStakeholder, typeNotification: 2 }))) ||
        {};
      if (!notificationCreated) {
        throw new Error("No se envió la notificación");
      }

      await hideToast();
      presentToast({
        message: `Se envió una notificación a ${documentStakeholder.stakeholder?.name} para que firme el acuerdo`,
        color: "success",
        duration: 8000,
      });
    } catch (error) {
      await hideToast();
      presentToast({
        message: "Ocurrió un problema enviando notificación al firmante",
        color: "danger",
        duration: 8000,
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      navigator.clipboard.writeText(urlNotify);
      await hideToast();
      presentToast({
        message: `Enlace copiado exitosamente`,
        color: "success",
        duration: 8000,
      });
    } catch (err) {
      presentToast({
        message: "Ocurrió un problema copiando enlace",
        color: "danger",
        duration: 8000,
      });
    }
  };

  return (
    <>
      <IonButton
        size="small"
        fill="solid"
        color="tertiary"
        onClick={() => {
          setShowNotifyModal(true);
        }}
      >
        <IonIcon icon={notificationsOutline} slot="start"></IonIcon>
        <IonText class="ion-hide-sm-down">Notificar</IonText>
      </IonButton>

      <IonModal isOpen={showNotifyModal} onWillDismiss={() => setShowNotifyModal(false)}>
        <IonToolbar>
          <IonTitle>Notificar a firmante</IonTitle>
        </IonToolbar>
        <IonContent>
          <h4 className="ion-padding-start">
            <IonText>Opciones</IonText>
          </h4>
          <IonItem>
            <IonLabel class="ion-text-nowrap">
              <h2 style={{ fontWeight: 500 }}>Enviar correo</h2>
              <p>
                Enviar enlace al correo del firmante
                <strong> ({documentStakeholder?.stakeholder?.email})</strong>
              </p>
            </IonLabel>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  handleSendNotification();
                }}
                fill="solid"
                color="primary"
              >
                <span>Enviar</span>
                <IonIcon icon={mailOutline} slot="end"></IonIcon>
              </IonButton>
            </IonButtons>
          </IonItem>

          <IonItem>
            <IonLabel>
              <h2 style={{ fontWeight: 500 }}>Obtener enlace para envío manual</h2>
              <p>
                <strong>{urlNotify}</strong>
              </p>
            </IonLabel>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  handleCopyLink();
                }}
                fill="solid"
                color="primary"
              >
                <span>Copiar</span>
                <IonIcon icon={copyOutline} slot="end"></IonIcon>
              </IonButton>
            </IonButtons>
          </IonItem>
          <IonItem>
            <IonLabel class="ion-text-wrap">
              <h2 style={{ fontWeight: 500 }}>Enviar notificación de este acuerdo por WhatsApp</h2>
              <p>Enviar enlace al teléfono del firmante vía WhastApp.</p>
            </IonLabel>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  handleSendWhatsappNotification();
                }}
                fill="solid"
                color="primary"
                disabled={state.notifyByWhatsappStatus === "pending"}
              >
                <span>Enviar</span>
                <IonIcon icon={logoWhatsapp} slot="end"></IonIcon>
              </IonButton>
            </IonButtons>
          </IonItem>
          <IonItem>
            <IonLabel class="ion-text-wrap">
              <h2 style={{ fontWeight: 500 }}>
                Enviar notificación de acuerdos pendientes por WhatsApp
              </h2>
              <p>Enviar enlace al teléfono del firmante vía WhastApp.</p>
            </IonLabel>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  handleSendAllWhatsappNotification();
                }}
                fill="solid"
                color="primary"
                disabled={state.notifyByWhatsappStatus === "pending"}
              >
                <span>Enviar</span>
                <IonIcon icon={logoWhatsapp} slot="end"></IonIcon>
              </IonButton>
            </IonButtons>
          </IonItem>
        </IonContent>
      </IonModal>
    </>
  );
};
